import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Tags, Plus, Trash2, Loader2, ShieldAlert } from "lucide-react";

export default function Keywords() {
  const [keywords, setKeywords] = useState([]);
  const [word, setWord] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/keywords").then(({ data }) => setKeywords(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (word.trim().length < 2) { toast.error("Keyword minimal 2 karakter"); return; }
    setSaving(true);
    try {
      await api.post("/keywords", { word: word.trim() });
      toast.success("Keyword ditambahkan");
      setWord("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (k, active) => {
    try { await api.patch(`/keywords/${k.id}`, { active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (k) => {
    try { await api.delete(`/keywords/${k.id}`); toast.success("Keyword dihapus"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2">
          <Tags className="text-[#3B82F6]" size={30} /> Keyword Manager
        </h1>
        <p className="text-[#9CA3AF] mt-1 text-sm">Kata yang cocok di ketikan akan dihitung sebagai <span className="text-[#F87171]">badwords</span> di dashboard & viewer.</p>
      </div>

      <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 flex gap-2 mb-5">
        <Input
          data-testid="keyword-input"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Tambah keyword (mis. berlin, judi)..."
          className="bg-[#0B0F17] border-[#1F2937] text-white"
        />
        <Button data-testid="keyword-add-button" onClick={add} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] shrink-0">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <><Plus size={16} className="mr-1" /> Tambah</>}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#3B82F6]" size={28} /></div>
      ) : keywords.length === 0 ? (
        <div className="text-center py-16 text-[#6B7280] bg-[#0B0F17] border border-[#1F2937] rounded-xl">
          <ShieldAlert size={40} className="mx-auto mb-3 opacity-40" />Belum ada keyword. Tambahkan di atas.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {keywords.map((k, i) => (
            <motion.div
              key={k.id}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              data-testid={`keyword-item-${k.id}`}
              className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 flex items-center justify-between gap-3"
            >
              <span className={`font-mono font-semibold ${k.active ? "text-white" : "text-[#6B7280] line-through"}`}>{k.word}</span>
              <div className="flex items-center gap-3">
                <Switch data-testid={`keyword-toggle-${k.id}`} checked={k.active} onCheckedChange={(v) => toggle(k, v)} />
                <button data-testid={`keyword-delete-${k.id}`} onClick={() => remove(k)} className="text-[#9CA3AF] hover:text-[#EF4444]"><Trash2 size={16} /></button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
